/**
 * Calendar dates (YYYY-MM-DD) from the API are the customer's chosen local day.
 * `new Date('2026-03-12')` parses as UTC midnight → displays as previous calendar day in US/CA.
 * Parsing as local noon keeps labels aligned with the stored date.
 */

const DATE_ONLY = /^(\d{4}-\d{2}-\d{2})$/;

/** Today in the user's local timezone as YYYY-MM-DD (for date input min, comparisons). */
export function localYyyyMmDd(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse a date-only string as a stable local calendar day (noon). Full ISO timestamps unchanged. */
export function parseCalendarOrIso(dateStr: string): Date {
  const s = String(dateStr).trim();
  if (DATE_ONLY.test(s)) return new Date(`${s}T12:00:00`);
  return new Date(s);
}

/** Format date-only or ISO for display (en-CA). Use for pickup_date and full timestamps. */
export function formatCalendarDateEnCA(
  dateStr: string,
  opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }
): string {
  return parseCalendarOrIso(dateStr).toLocaleDateString('en-CA', opts);
}

/** Store clock for pack-slip "Printed at" (production server is UTC; Tammy is America/Toronto). */
export function formatTorontoDateTime(now: Date = new Date()): { dateStr: string; timeStr: string } {
  const dateStr = now.toLocaleDateString('en-CA', {
    timeZone: 'America/Toronto',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('en-CA', {
    timeZone: 'America/Toronto',
    hour: 'numeric',
    minute: '2-digit',
  });
  return { dateStr, timeStr };
}

/** YYYY-MM-DD promos/closures — label matches stored calendar day. ISO timestamps → local instant. */
export function formatAnyDateDisplay(
  dateStr: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }
): string {
  if (dateStr == null || String(dateStr).trim() === '') return '—';
  const part = String(dateStr).trim().split('T')[0];
  if (DATE_ONLY.test(part)) {
    return new Date(`${part}T12:00:00`).toLocaleDateString('en-CA', opts);
  }
  return new Date(dateStr).toLocaleDateString('en-CA', opts);
}

/** Whole days from local today to a calendar end date. Negative if already past. */
export function calendarDaysFromToday(dateStr: string): number {
  const part = String(dateStr).trim().split('T')[0];
  if (!DATE_ONLY.test(part)) return NaN;
  const today = localYyyyMmDd();
  const t0 = new Date(`${today}T12:00:00`).getTime();
  const t1 = new Date(`${part}T12:00:00`).getTime();
  return Math.round((t1 - t0) / (24 * 60 * 60 * 1000));
}

/** Add N calendar days to a YYYY-MM-DD string (local). */
export function addCalendarDays(yyyyMmDd: string, days: number): string {
  const d = new Date(`${yyyyMmDd.split('T')[0]}T12:00:00`);
  d.setDate(d.getDate() + days);
  return localYyyyMmDd(d);
}
